import { GetServerSideProps, NextPage } from "next";
import Featurette from "../../components/Featurette";
import { CARD_IMAGE_PLACEMENTS, FEATURETTE_IMAGE_PLACEMENTS } from "../../lib/constants";
import FeaturetteTextContent from "../../components/Featurette/FeaturetteTextContent";
import { ArticleDTO, CategoryDTO } from "../../lib/types";
import Card from "../../components/Card";
import ArticleRecommendation from "../../components/ArticleRecommendation";
import { RichText } from "../../components/RichText";
import Pill from "../../components/Pill";
import Author from "../../components/BlogPost/Author";
import format from "date-fns/format";
import { mapArticleData } from "../../lib/utils/mappers";
import api from "../../lib/utils/api";
import { useAppContext } from "../../context/shared";
import { Col, Container, Row } from "react-bootstrap";
import cmsEndpoints from "../../lib/utils/cmsEndpoints";
import { PageHead } from "../../components/PageHead";


export const getServerSideProps: GetServerSideProps = async (context) => {
  const slug = context.params?.slug as string
  const articleData = await api.get(cmsEndpoints.blogPost({ slug }))
  if (articleData?.data.data.length === 0) return { notFound: true }
  const article = mapArticleData(articleData?.data.data[0].attributes, context.locale as any)
  const relatedArticlesData = await api.get(cmsEndpoints.relatedBlogPosts(article.categories || []))
  const relatedArticles = (relatedArticlesData?.data.data || []).map((article: any) => {
    return mapArticleData(article.attributes, context.locale as any)
  })
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  )
  return {
    props: {
      relatedArticles,
      article
    }
  }
}

const BlogPost: NextPage = (props: any) => {
  const { localization } = useAppContext()
  return (
    <div>
      <PageHead title={props.article?.title} description={props.article?.intro} image={props.article?.mainImage} />
      <Featurette
        image={props.article.mainImage}
        imagePlacement={FEATURETTE_IMAGE_PLACEMENTS.RIGHT}
        backgroundColor='#FAFCFC'
        mobileFullscreenImage={true}
      >
        <div className='h-100 w-100 d-flex align-items-center justify-content-center justify-content-md-start'>
          <FeaturetteTextContent
            title={props.article.title}
            body={props.article.intro}
          />
        </div>
      </Featurette>
      <Container className="py-5">
        <Row className='justify-content-center'>
          <Col sm='12' lg='10'>
            <RichText content={props.article.content} />
          </Col>
          <Col sm='12' lg='10'>
            {props.article.categories.length > 0 && (
              <div className='d-flex gap-2 pt-4 mb-4'>
                {props.article.categories.map((tag: CategoryDTO, index: number) => (
                  <Pill key={index} content={tag.name} variant='secondary' />
                ))}
              </div>
            )}
            <hr />
          </Col>
          <Col sm='12' lg='10'>
            <div className={'py-5'}> <i className="caption text-muted">{localization.blog_published_on} {format(new Date(props.article.publishedAt), 'yyyy. MMMM dd.')}</i></div>
            {props?.article?.author &&
              <div className='pb-5'>
                <Author avatar={props.article.author.image} name={props.article.author.name} bio={props.article.author.bio} id={props.article.author.id} />
              </div>
            }
            <hr />
          </Col>
          <Col md='12'>
            {props.relatedArticles.length > 0 && (
              <ArticleRecommendation title={localization.blog_related_articles_list}>
                {props.relatedArticles.map((article: ArticleDTO, index: number) => (
                  <Col sm='12' md='6' lg='4' className="mb-4" key={index}>
                    <Card
                      key={index}
                      title={article.title}
                      body={article.intro}
                      buttonText='Read more'
                      image={article.mainImage}
                      imagePlacement={CARD_IMAGE_PLACEMENTS.TOP_FULL}
                      tags={article.categories}
                      link={`/blog/${article.slug}`}
                    />
                  </Col>
                ))}
              </ArticleRecommendation>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default BlogPost
